import type { UnitRateCard } from './pricing';
import type { BlockHolder } from '../api/masters';
import { matchesGarmentCode } from '../units/garmentCode';
import { normalizeReference } from '../units/referenceNumber';

/**
 * The rail the booking counter picks from: what is on it, what is free, and how to narrow it.
 *
 * <p>
 * Pure and separate from the screen for the reason `draft.ts` and `pricing.ts` are — the counting is
 * the part a shop reads before anything else ("two of nine left for that weekend"), and a number
 * that only exists inside a component is a number nothing can check.
 * </p>
 */

/** A garment the availability engine has answered for, in the shape the counter renders. */
export interface AvailableUnit {
  unitId: number;
  serialCode: string;
  /** The shop's own number, shown "RN-7" beside the serial (core `units/referenceNumber.ts`). */
  referenceNumber?: string | null;

  itemId: number;
  itemName: string;
  /**
   * The design's category, carried per garment so the rail can be filtered by it.
   *
   * <p>
   * It comes from the design list rather than from the availability answer, which is about dates and
   * knows nothing about the catalogue tree. A garment whose design is missing from that list — the
   * catalogue is capped at the designs one search spans — keeps a null id and sits under
   * "Uncategorised" rather than vanishing from a filtered rail.
   * </p>
   */
  categoryId: number | null;
  categoryName: string;
  variantDisplayName: string;
  /**
   * The card that prices this garment, resolved server-side through unit → variant → item →
   * category with the modifier that survives that level already reduced to one percentage.
   *
   * Per garment rather than per design, which is the whole point: the newer of two identical
   * sherwanis can carry "+15%" and the rail has to show it.
   */
  rateCard: UnitRateCard;
  /**
   * This garment's own photograph, or the design's, or null.
   *
   * The card was text-only, which on a rail of eleven navy tuxedos means eleven cards that differ
   * by three characters of serial code. The theme's own POS grid puts a picture on every tile for
   * exactly this reason.
   */
  imageUrl?: string | null;
  /** Set when the unit is committed elsewhere — shown rather than hidden, with the date it frees. */
  heldBy?: HeldGarment | null;
}

/**
 * What is in the way of a garment, at the counter.
 *
 * <p>
 * <strong>`tentative` is the field that matters here.</strong> A garment somebody is *holding* is
 * one phone call from being free, and one somebody has *booked* is not — but both place the same
 * kind of calendar row, so a screen reading the block type calls both "Committed" and the shop turns
 * away a customer who was ready to pay. With the party attached, the clerk can ring the first
 * customer while the second is still standing there.
 * </p>
 */
export interface HeldGarment {
  tentative: boolean;
  /** The first day the thing in the way covers, so the drawer can say "holds 10–14 Dec". */
  fromDate: string;
  /** The last day the thing in the way runs to. */
  untilDate: string;
  /** "Back on the 15th" — empty when nothing in the calendar clears. */
  freeOn: string;
  /** Who is holding it, when a booking placed the block. Null for cleaning, maintenance and the like. */
  holder: BlockHolder | null;
  /** What is in the way when nobody is — "Cleaning buffer", "Maintenance". */
  reason: string;
}

/** One category on the rail, with what is free out of what is there. */
export interface CategoryTally {
  /** Null for a garment whose design was not in the searched catalogue. */
  id: number | null;
  name: string;
  free: number;
  total: number;
}

/** A garment nobody has a claim on for these dates. */
export function isFree(unit: AvailableUnit): boolean {
  return !unit.heldBy;
}

/**
 * Free on the dates and not already in the booking on screen — what a chip counts. A garment the
 * clerk has just picked is free to the server and gone to the counter; counting it would leave
 * "Jodhpuri 1/2" on the chip while the card beside it says none is left.
 */
export function freeToPick(units: readonly AvailableUnit[], claimed?: ReadonlySet<number>): number {
  return units.filter((unit) => isFree(unit) && !claimed?.has(unit.unitId)).length;
}

/**
 * The categories the rail actually holds garments in, each with its free-of-total count. Garments
 * already in the booking on screen (`claimed`) are counted as gone, as the design cards count them.
 *
 * <p>
 * <strong>Built from the search results, not from the category master.</strong> A tree of nineteen
 * categories with stock in four is a filter that mostly leads to empty screens; this way every chip
 * on the counter is a chip with something behind it.
 * </p>
 *
 * <p>
 * Sorted by name so the row does not reshuffle between searches — a control that moves under the
 * finger is worse than one in an arbitrary order.
 * </p>
 */
export function categoriesOnRail(
  units: readonly AvailableUnit[],
  claimed?: ReadonlySet<number>,
): CategoryTally[] {
  const tally = new Map<number | null, CategoryTally>();

  for (const unit of units) {
    const row = tally.get(unit.categoryId) ?? {
      id: unit.categoryId,
      name: unit.categoryName || 'Uncategorised',
      free: 0,
      total: 0,
    };

    row.total += 1;

    if (isFree(unit) && !claimed?.has(unit.unitId)) {
      row.free += 1;
    }

    tally.set(unit.categoryId, row);
  }

  return [...tally.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The rail narrowed to one category and one search box.
 *
 * <p>
 * Filtered in the browser, unlike the customer search: these are the results of one availability
 * call the parent already made, so re-asking the server to narrow them would be a round trip to
 * filter a list that is by construction at most a shop's whole rail.
 * </p>
 *
 * <p>
 * <strong>A committed garment is never filtered out for being committed.</strong> "No, but it is
 * back on the 15th" is what a counter clerk needs, and hiding held stock is what sends a customer to
 * the shop next door.
 * </p>
 */
export function visibleGarments(
  units: readonly AvailableUnit[],
  { category, query }: { category: number | null; query: string },
): AvailableUnit[] {
  const needle = query.trim().toLowerCase();

  return units.filter((unit) => {
    if (category !== null && unit.categoryId !== category) {
      return false;
    }

    if (!needle) {
      return true;
    }

    // A garment code typed the short way ("bjb36-1") finds its garment, as the stock search does,
    // and so does the shop's own number typed whole ("7", "007" or "RN-7").
    return (
      `${unit.itemName} ${unit.variantDisplayName} ${unit.serialCode}`
        .toLowerCase()
        .includes(needle) ||
      matchesGarmentCode(unit.serialCode, needle) ||
      (!!unit.referenceNumber && normalizeReference(needle) === unit.referenceNumber)
    );
  });
}
